import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Home, Users, PlusCircle, Truck, Wallet, RefreshCw, MinusCircle, Tag, Trash2 } from "lucide-react";
import RequireAuth from "../components/RequireAuth";
import { authApi, logout, PLAN_LABELS } from "../lib/auth";
import { LOGO_URL, BUSINESS_NAME } from "../lib/site";

const PLAN_AMOUNTS = { basic: 5000, growth: 10000, scale: 25000 };

function AdminContent({ user }) {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pricing, setPricing] = useState({ pricing: {}, print_types: {} });

  // credit form
  const [creditBrand, setCreditBrand] = useState("");
  const [creditPlan, setCreditPlan] = useState("basic");
  const [creditAmount, setCreditAmount] = useState(PLAN_AMOUNTS.basic);

  const onPlanChange = (plan) => {
    setCreditPlan(plan);
    setCreditAmount(PLAN_AMOUNTS[plan]);
  };

  // charge form
  const [chargeBrand, setChargeBrand] = useState("");
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeNote, setChargeNote] = useState("");

  // custom rate form
  const [rateBrand, setRateBrand] = useState("");
  const [rateFabric, setRateFabric] = useState("");
  const [ratePrintType, setRatePrintType] = useState("");
  const [ratePrice, setRatePrice] = useState("");

  // order form
  const [orderBrand, setOrderBrand] = useState("");
  const [orderFabric, setOrderFabric] = useState("");
  const [orderPrintType, setOrderPrintType] = useState("");
  const [orderQty, setOrderQty] = useState(1);

  const refresh = useCallback(() => {
    authApi.get("/admin/brands").then((r) => setBrands(r.data)).catch(() => {});
    authApi.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    authApi.get("/pod/pricing").then((r) => {
      setPricing(r.data);
      const fabrics = Object.keys(r.data.pricing || {});
      const ptypes = Object.keys(r.data.print_types || {});
      if (fabrics[0]) { setOrderFabric(fabrics[0]); setRateFabric(fabrics[0]); }
      if (ptypes[0]) { setOrderPrintType(ptypes[0]); setRatePrintType(ptypes[0]); }
    }).catch(() => {});
  }, [refresh]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const submitCredit = async () => {
    if (!creditBrand) return toast.error("Select a brand");
    try {
      const res = await authApi.post("/admin/credit", {
        user_id: creditBrand,
        plan: creditPlan,
        amount: creditAmount === "" ? null : Number(creditAmount),
      });
      toast.success(`Wallet credited. New balance ₹${res.data.balance}`);
      setCreditAmount("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to credit");
    }
  };

  const submitCharge = async (full = false) => {
    if (!chargeBrand) return toast.error("Select a brand");
    if (!full && (!chargeAmount || Number(chargeAmount) <= 0)) return toast.error("Enter an amount or use Charge Full Balance");
    try {
      const res = await authApi.post("/admin/charge", {
        user_id: chargeBrand,
        amount: full ? null : Number(chargeAmount),
        full,
        note: chargeNote,
      });
      toast.success(`Charged ₹${res.data.charged}. Balance now ₹${res.data.balance}`);
      setChargeAmount("");
      setChargeNote("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to charge");
    }
  };

  const submitRate = async (remove = false) => {
    if (!rateBrand) return toast.error("Select a brand");
    if (!rateFabric || !ratePrintType) return toast.error("Select fabric and print type");
    if (!remove && (!ratePrice || Number(ratePrice) <= 0)) return toast.error("Enter a custom price");
    try {
      await authApi.post("/admin/custom-rate", {
        user_id: rateBrand,
        fabric: rateFabric,
        print_type: ratePrintType,
        price: remove ? null : Number(ratePrice),
      });
      toast.success(remove ? "Custom rate removed" : "Custom rate saved to brand");
      setRatePrice("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rate");
    }
  };

  const removeRate = async (uid, key) => {
    const [fabric, print_type] = key.split("|");
    try {
      await authApi.post("/admin/custom-rate", { user_id: uid, fabric, print_type, price: null });
      toast.success("Custom rate removed");
      refresh();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const submitOrder = async () => {
    if (!orderBrand) return toast.error("Select a brand");
    try {
      await authApi.post("/admin/orders", {
        user_id: orderBrand,
        fabric: orderFabric,
        print_type: orderPrintType,
        quantity: Number(orderQty),
      });
      toast.success("Order created (pending). Mark it shipped to deduct the wallet.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    }
  };

  const shipOrder = async (orderId) => {
    try {
      const res = await authApi.post(`/admin/orders/${orderId}/ship`);
      toast.success(`Shipped. Brand balance now ₹${res.data.balance}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to ship");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Delete this pending order? This cannot be undone.")) return;
    try {
      await authApi.delete(`/admin/orders/${orderId}`);
      toast.success("Order deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const brandLabel = (b) => (b?.brand_name ? `${b.brand_name} (${b.email})` : b?.email || "");
  const brandName = (uid) => { const b = brands.find((x) => x.user_id === uid); return b ? brandLabel(b) : uid; };

  const [nameEdits, setNameEdits] = useState({});
  const saveBrandName = async (uid) => {
    try {
      await authApi.post("/admin/brands/name", { user_id: uid, brand_name: nameEdits[uid] ?? "" });
      toast.success("Display name saved");
      setNameEdits((s) => { const n = { ...s }; delete n[uid]; return n; });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save name"); }
  };

  const fabrics = Object.keys(pricing.pricing || {});
  const printTypes = Object.entries(pricing.print_types || {});

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt={BUSINESS_NAME} className="w-9 h-9 rounded-full border border-slate-700" />
            <span className="font-extrabold uppercase tracking-tight text-sm">POD Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={refresh} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-[#FFD400] text-slate-950 px-3 py-2" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <Link to="/admin-shipments" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide hover:text-[#FFD400]" data-testid="goto-shipments"><Truck className="w-4 h-4" /> Shipments</Link>
            <Link to="/" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide hover:text-[#FFD400]"><Home className="w-4 h-4" /> Home</Link>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-slate-800 hover:bg-slate-700 px-3 py-2"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <h1 className="text-2xl font-extrabold uppercase tracking-tight">POD Wallet Management</h1>

        {/* Action forms */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Credit wallet */}
          <div className="bg-white border-2 border-slate-950 p-6 space-y-4">
            <h2 className="font-extrabold uppercase flex items-center gap-2"><Wallet className="w-5 h-5" /> Credit Wallet</h2>
            <select value={creditBrand} onChange={(e) => setCreditBrand(e.target.value)} data-testid="credit-brand" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
              <option value="">Select brand…</option>
              {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{brandLabel(b)} (₹{b.wallet_balance})</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select value={creditPlan} onChange={(e) => onPlanChange(e.target.value)} data-testid="credit-plan" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                <option value="basic">Basic (₹5,000)</option>
                <option value="growth">Growth (₹10,000)</option>
                <option value="scale">Scale (₹25,000)</option>
              </select>
              <input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder={`₹${PLAN_AMOUNTS[creditPlan]}`} data-testid="credit-amount" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium" />
            </div>
            <p className="text-[11px] text-slate-500 font-mono">Amount auto-fills from the selected plan. Exactly this amount is added — no bonus is applied automatically. Set amount to 0 to only upgrade the plan without adding funds.</p>
            <button onClick={submitCredit} data-testid="credit-submit" className="w-full bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-4 py-3 border border-slate-950">Credit Wallet</button>
          </div>

          {/* Charge wallet */}
          <div className="bg-white border-2 border-slate-950 p-6 space-y-4">
            <h2 className="font-extrabold uppercase flex items-center gap-2"><MinusCircle className="w-5 h-5" /> Charge Wallet</h2>
            <select value={chargeBrand} onChange={(e) => setChargeBrand(e.target.value)} data-testid="charge-brand" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
              <option value="">Select brand…</option>
              {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{brandLabel(b)} (₹{b.wallet_balance})</option>)}
            </select>
            <input type="number" value={chargeAmount} onChange={(e) => setChargeAmount(e.target.value)} placeholder="Amount (₹)" data-testid="charge-amount" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium" />
            <input type="text" value={chargeNote} onChange={(e) => setChargeNote(e.target.value)} placeholder="Reason (e.g. design charge, shipping, settlement)" data-testid="charge-note" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium" />
            <p className="text-[11px] text-slate-500 font-mono">Use for additional services or to settle the account by charging the full balance.</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => submitCharge(false)} data-testid="charge-submit" className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-4 py-3 border border-slate-950">Charge</button>
              <button onClick={() => submitCharge(true)} data-testid="charge-full" className="bg-rose-600 text-white hover:bg-rose-700 text-sm font-bold uppercase tracking-wider px-4 py-3 border border-slate-950">Charge Full</button>
            </div>
          </div>

          {/* Create order */}
          <div className="bg-white border-2 border-slate-950 p-6 space-y-4">
            <h2 className="font-extrabold uppercase flex items-center gap-2"><PlusCircle className="w-5 h-5" /> Create Order</h2>
            <select value={orderBrand} onChange={(e) => setOrderBrand(e.target.value)} data-testid="order-brand" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
              <option value="">Select brand…</option>
              {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{brandLabel(b)} ({PLAN_LABELS[b.plan]})</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select value={orderFabric} onChange={(e) => setOrderFabric(e.target.value)} data-testid="order-fabric" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                {fabrics.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <input type="number" min="1" value={orderQty} onChange={(e) => setOrderQty(e.target.value)} data-testid="order-qty" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium" />
            </div>
            <select value={orderPrintType} onChange={(e) => setOrderPrintType(e.target.value)} data-testid="order-printtype" className="w-full border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
              {printTypes.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={submitOrder} data-testid="order-submit" className="w-full bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-4 py-3 border border-slate-950">Create Order</button>
          </div>
        </div>

        {/* Custom Rates */}
        <section>
          <h2 className="text-lg font-extrabold uppercase tracking-tight mb-4 flex items-center gap-2"><Tag className="w-5 h-5" /> Custom Rates (per brand)</h2>
          <div className="bg-white border-2 border-slate-950 p-6 space-y-4" data-testid="custom-rate-form">
            <p className="text-[13px] text-slate-600 font-medium">Set an agreed all-inclusive per-piece rate for a specific brand. Their orders will automatically use this rate (no plan discount or ₹15 processing is added on custom-rated items).</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select value={rateBrand} onChange={(e) => setRateBrand(e.target.value)} data-testid="rate-brand" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                <option value="">Select brand…</option>
                {brands.filter((b) => b.role !== "admin").map((b) => <option key={b.user_id} value={b.user_id}>{brandLabel(b)}</option>)}
              </select>
              <select value={rateFabric} onChange={(e) => setRateFabric(e.target.value)} data-testid="rate-fabric" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                {fabrics.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select value={ratePrintType} onChange={(e) => setRatePrintType(e.target.value)} data-testid="rate-printtype" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium bg-white">
                {printTypes.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input type="number" value={ratePrice} onChange={(e) => setRatePrice(e.target.value)} placeholder="Custom ₹/pc" data-testid="rate-price" className="border-2 border-slate-950 px-3 py-2.5 text-sm font-medium" />
            </div>
            <button onClick={() => submitRate(false)} data-testid="rate-submit" className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-6 py-3 border border-slate-950">Save Custom Rate</button>

            {/* Existing custom rates for the selected brand */}
            {rateBrand && (() => {
              const b = brands.find((x) => x.user_id === rateBrand);
              const cp = (b && b.custom_pricing) || {};
              const entries = Object.entries(cp);
              return (
                <div className="pt-2" data-testid="custom-rate-list">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Current custom rates for {b ? brandLabel(b) : ""}</p>
                  {entries.length === 0 ? (
                    <p className="text-sm text-slate-500">No custom rates set. This brand uses standard pricing.</p>
                  ) : (
                    <div className="border-2 border-slate-200 divide-y divide-slate-200">
                      {entries.map(([key, price]) => {
                        const [fab, pt] = key.split("|");
                        return (
                          <div key={key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                            <span className="font-medium">{fab} · <span className="text-slate-500">{pricing.print_types?.[pt] || pt}</span></span>
                            <div className="flex items-center gap-3">
                              <span className="font-extrabold font-mono">₹{price}/pc</span>
                              <button onClick={() => removeRate(rateBrand, key)} data-testid={`rate-remove-${key}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </section>

        {/* Brands table */}
        <section>
          <h2 className="text-lg font-extrabold uppercase tracking-tight mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Brands ({brands.filter((b) => b.role !== "admin").length})</h2>
          <div className="bg-white border-2 border-slate-950 overflow-x-auto" data-testid="brands-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-950 text-white text-left text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-3">Email</th><th className="px-4 py-3">Display Name</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Balance</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {brands.filter((b) => b.role !== "admin").map((b) => (
                  <tr key={b.user_id}>
                    <td className="px-4 py-3 font-medium">{b.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={nameEdits[b.user_id] ?? b.brand_name ?? ""}
                          onChange={(e) => setNameEdits((s) => ({ ...s, [b.user_id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") saveBrandName(b.user_id); }}
                          placeholder="e.g. Bliss Tees"
                          data-testid={`brand-name-input-${b.user_id}`}
                          className="border-2 border-slate-300 focus:border-slate-950 px-2 py-1.5 text-sm w-40"
                        />
                        <button
                          onClick={() => saveBrandName(b.user_id)}
                          disabled={(nameEdits[b.user_id] ?? b.brand_name ?? "") === (b.brand_name ?? "")}
                          data-testid={`brand-name-save-${b.user_id}`}
                          className="text-xs font-bold uppercase bg-slate-950 text-white px-3 py-1.5 disabled:opacity-30 hover:bg-slate-800"
                        >Save</button>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="text-xs font-bold uppercase">{PLAN_LABELS[b.plan]}</span></td>
                    <td className="px-4 py-3 text-right font-extrabold font-mono">₹{b.wallet_balance}</td>
                  </tr>
                ))}
                {brands.filter((b) => b.role !== "admin").length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500">No brands yet. Brands appear here after they log in via the portal.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Orders */}
        <section>
          <h2 className="text-lg font-extrabold uppercase tracking-tight mb-4 flex items-center gap-2"><Truck className="w-5 h-5" /> Orders ({orders.length})</h2>
          <div className="bg-white border-2 border-slate-950" data-testid="admin-orders">
            {orders.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No orders yet.</p>
            ) : (
              <div className="divide-y divide-slate-200">
                {orders.map((o) => (
                  <div key={o.order_id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-slate-900">{brandName(o.user_id)}</p>
                      <p className="text-xs text-slate-500 font-mono">{o.quantity} × {o.fabric} · {o.print_type_label}</p>
                      <p className="text-[11px] text-slate-400 font-mono mt-0.5">Placed: {o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}{o.status === "shipped" && o.shipped_at ? ` · Shipped: ${new Date(o.shipped_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-extrabold font-mono">₹{o.total}</p>
                        {o.discount_rate > 0 && <p className="text-[10px] text-emerald-600 font-bold">5% off applied</p>}
                        {o.is_custom && <p className="text-[10px] text-[#b58900] font-bold">custom rate</p>}
                      </div>
                      {o.status === "shipped" ? (
                        <span className="text-xs font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-3 py-1.5">Shipped</span>
                      ) : (
                        <>
                          <button onClick={() => shipOrder(o.order_id)} data-testid={`ship-${o.order_id}`} className="flex items-center gap-1.5 bg-[#FFD400] text-slate-950 text-xs font-bold uppercase tracking-wider px-4 py-2 border border-slate-950 hover:bg-[#ffdb33]">
                            <Truck className="w-4 h-4" /> Mark Shipped
                          </button>
                          <button onClick={() => deleteOrder(o.order_id)} data-testid={`delete-${o.order_id}`} title="Delete order" className="flex items-center gap-1.5 bg-white text-rose-600 text-xs font-bold uppercase tracking-wider px-3 py-2 border-2 border-rose-600 hover:bg-rose-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default function AdminPod() {
  return (
    <RequireAuth adminOnly redirectTarget="/admin-pod">
      {(user) => <AdminContent user={user} />}
    </RequireAuth>
  );
}
